import * as THREE from "three";

const bilinearInterpolate = (x, y, width, height, data) => {
  const x0 = Math.floor(x);
  const x1 = Math.min(x0 + 1, width - 1);
  const y0 = Math.floor(y);
  const y1 = Math.min(y0 + 1, height - 1);
  const dx = x - x0;
  const dy = y - y0;
  const i00 = (y0 * width + x0) * 2;
  const i01 = (y1 * width + x0) * 2;
  const i10 = (y0 * width + x1) * 2;
  const i11 = (y1 * width + x1) * 2;
  const v00 = new THREE.Vector2(data[i00], data[i00 + 1]);
  const v01 = new THREE.Vector2(data[i01], data[i01 + 1]);
  const v10 = new THREE.Vector2(data[i10], data[i10 + 1]);
  const v11 = new THREE.Vector2(data[i11], data[i11 + 1]);
  const v0 = v00.clone().lerp(v10, dx);
  const v1 = v01.clone().lerp(v11, dx);

  return v0.lerp(v1, dy);
};

const smooth = (data, columns, rows, k = 2, sigma = 2) => {
  // Gaussian smoothing

  const horizontal = new Float32Array(data.length);
  const halfRadius = Math.round(3 * sigma);

  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < columns; x++) {
      let totalWeight = 0;
      const sums = new Array(k).fill(0);

      for (let d = -halfRadius; d <= halfRadius; d++) {
        if (x + d < 0 || x + d >= columns) continue;

        const weight = Math.exp((-d * d) / (sigma * sigma));
        totalWeight += weight;

        for (let i = 0; i < k; i++) {
          const index = k * (y * columns + (x + d)) + i;
          sums[i] += data[index] != null ? weight * data[index] : 0;
        }
      }

      for (let i = 0; i < k; i++) {
        horizontal[k * (y * columns + x) + i] =
          totalWeight < 0.001 ? 0 : sums[i] / totalWeight;
      }
    }
  }

  const final = new Float32Array(data.length);

  for (let x = 0; x < columns; x++) {
    for (let y = 0; y < rows; y++) {
      let totalWeight = 0;
      const sums = new Array(k).fill(0);

      for (let d = -halfRadius; d <= halfRadius; d++) {
        if (y + d < 0 || y + d >= rows) continue;

        const weight = Math.exp((-d * d) / (sigma * sigma));
        totalWeight += weight;

        for (let i = 0; i < k; i++) {
          const index = k * ((y + d) * columns + x) + i;
          sums[i] += horizontal[index] != null ? weight * horizontal[index] : 0;
        }
      }

      for (let i = 0; i < k; i++) {
        final[k * (y * columns + x) + i] =
          totalWeight < 0.001 ? 0 : sums[i] / totalWeight;
      }
    }
  }

  return final;
};

const smoothOld = (data, columns, rows, sigma = 2) => {
  // Gaussian smoothingOld

  const horizontal = new Float32Array(data.length);
  const halfRadius = Math.round(3 * sigma);

  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < columns; x++) {
      let totalWeight = 0;
      let s0 = 0;
      let s1 = 0;

      for (let d = -halfRadius; d <= halfRadius; d++) {
        if (x + d < 0 || x + d >= columns) continue;

        const weight = Math.exp((-d * d) / (sigma * sigma));
        totalWeight += weight;
        const index0 = 2 * (y * columns + (x + d)) + 0;
        const index1 = 2 * (y * columns + (x + d)) + 1;
        s0 += data[index0] != null ? weight * data[index0] : 0;
        s1 += data[index1] != null ? weight * data[index1] : 0;
      }

      horizontal[2 * (y * columns + x) + 0] =
        totalWeight < 0.001 ? 0 : s0 / totalWeight;
      horizontal[2 * (y * columns + x) + 1] =
        totalWeight < 0.001 ? 0 : s1 / totalWeight;
    }
  }

  const final = new Float32Array(data.length);

  for (let x = 0; x < columns; x++) {
    for (let y = 0; y < rows; y++) {
      let totalWeight = 0;
      let s0 = 0;
      let s1 = 0;

      for (let d = -halfRadius; d <= halfRadius; d++) {
        if (y + d < 0 || y + d >= rows) continue;

        const weight = Math.exp((-d * d) / (sigma * sigma));
        totalWeight += weight;
        const index0 = 2 * ((y + d) * columns + x) + 0;
        const index1 = 2 * ((y + d) * columns + x) + 1;
        const value0 = horizontal[index0];
        const value1 = horizontal[index1];
        s0 += value0 != null ? weight * value0 : 0;
        s1 += value1 != null ? weight * value1 : 0;
      }

      final[2 * (y * columns + x) + 0] =
        totalWeight < 0.001 ? 0 : s0 / totalWeight;
      final[2 * (y * columns + x) + 1] =
        totalWeight < 0.001 ? 0 : s1 / totalWeight;
    }
  }

  return final;
};

const compute = {
  bilinearInterpolate,
  smooth,
  smoothOld,
};

export { compute };
